import React from "react";
import { useNavigate } from "react-router-dom";
import {
  FaUsers,
  FaClock,
  FaExclamationTriangle,
  FaCheckCircle,
  FaTimesCircle,
  FaUserCheck,
  FaArrowRight,
  FaCar,
  FaArrowUp,
  FaArrowDown,
  FaMapMarkerAlt,
  FaHandHoldingUsd,
} from "react-icons/fa";
import { calcPercentChange, formatSignedDiff } from "../utils/rideBreakdownComparison";

const statusConfig = {
  Registered: {
    gradient: "bg-gradient-to-r from-slate-500 to-slate-600",
    icon: FaUsers,
    textColor: "text-white",
    iconColor: "text-slate-200",
    description: "All filtered drivers",
  },
  Pending: {
    gradient: "bg-gradient-to-r from-yellow-400 to-yellow-500",
    icon: FaClock,
    textColor: "text-white",
    iconColor: "text-yellow-200",
    description: "Awaiting review",
  },
  Revisit: {
    gradient: "bg-gradient-to-r from-orange-400 to-orange-500",
    icon: FaExclamationTriangle,
    textColor: "text-white",
    iconColor: "text-orange-200",
    description: "Needs attention",
  },
  Verified: {
    gradient: "bg-gradient-to-r from-emerald-400 to-emerald-500",
    icon: FaCheckCircle,
    textColor: "text-white",
    iconColor: "text-emerald-200",
    description: "Approved & verified",
  },
  Rejected: {
    gradient: "bg-gradient-to-r from-red-400 to-red-500",
    icon: FaTimesCircle,
    textColor: "text-white",
    iconColor: "text-red-200",
    description: "Application declined",
  },
  Onboarded: {
    gradient: "bg-gradient-to-r from-blue-500 to-blue-600",
    icon: FaUserCheck,
    textColor: "text-white",
    iconColor: "text-blue-200",
    description: "Active & ready",
  },
  RideCompleted: {
    gradient: "bg-gradient-to-r from-teal-500 to-emerald-600",
    icon: FaCar,
    textColor: "text-white",
    iconColor: "text-teal-200",
    description: "1+ completed rides",
  },
  LocationNotAvailable: {
    gradient: "bg-gradient-to-r from-fuchsia-500 to-purple-600",
    icon: FaMapMarkerAlt,
    textColor: "text-white",
    iconColor: "text-fuchsia-200",
    description: "No current location on file",
  },
};

const DriverStatusSummaryCards = ({
  summaryData,
  comparisonData = null,
  showComparison = false,
  includeRegistered = false,
  activeStatus = "",
  onCardClick,
  onJoiningFeeClick,
  gridClassName = "",
  compact = false,
  loading = false,
}) => {
  const navigate = useNavigate();

  const formatCurrency = (value) =>
    new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 2,
    }).format(Number(value) || 0);

  const baseStatuses = [
    { label: "Pending", key: "Pending" },
    { label: "In Revisit", key: "Revisit" },
    { label: "Verified", key: "Verified" },
    { label: "Rejected", key: "Rejected" },
    { label: "Onboarded", key: "Onboarded" },
  ];

  const statuses = includeRegistered
    ? [
        { label: "Registered", key: "Registered" },
        ...baseStatuses,
        { label: "Ride Completed", key: "RideCompleted" },
        {
          label: "Current Location Not Available",
          key: "LocationNotAvailable",
        },
      ]
    : baseStatuses;

  const paidAmount = Number(summaryData?.joiningFeePaid || 0);
  const pendingAmount = Number(summaryData?.joiningFeeDue || 0);
  const totalJoiningFeeAmount = paidAmount + pendingAmount;
  const paidDriverCount = Number(summaryData?.JoiningFeeCount || 0);
  const pendingDriverCount = Number(summaryData?.joiningFeeDueCount || 0);

  const handleClick = (status) => {
    if (loading) return;
    if (typeof onCardClick === "function") {
      onCardClick(status);
      return;
    }
    if (status === "Registered") {
      navigate("/drivers");
      return;
    }
    navigate(`/drivers?status=${status}`);
  };

  const handleJoiningFeeSubClick = (filter) => {
    if (loading) return;
    if (typeof onJoiningFeeClick === "function") {
      onJoiningFeeClick(filter);
      return;
    }
    navigate(`/joining-fee?filter=${filter}`);
  };

  const denominator = Math.max(
    ...statuses.map((status) => Number(summaryData?.[status.key] || 0)),
    1
  );

  const classes = gridClassName ||
    (includeRegistered
      ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8"
      : "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6 mb-8");

  const cardClass = compact
    ? "rounded-lg p-3 shadow-md"
    : "rounded-2xl p-6 shadow-lg";

  const iconWrapClass = compact
    ? "p-2 rounded-full bg-white bg-opacity-20 backdrop-blur-sm"
    : "p-3 rounded-full bg-white bg-opacity-20 backdrop-blur-sm";

  const iconClass = compact ? "text-lg" : "text-2xl";
  const countClass = compact ? "text-3xl" : "text-4xl";
  const badgeClass = compact
    ? "bg-white bg-opacity-20 rounded-full px-1.5 py-0.5"
    : "bg-white bg-opacity-20 rounded-full px-2 py-1";
  const progressWrapClass = compact
    ? "mt-2 h-0.5 bg-white bg-opacity-20 rounded-full overflow-hidden"
    : "mt-4 h-1 bg-white bg-opacity-20 rounded-full overflow-hidden";

  const renderJoiningFeeCard = () => {
    const gradient = "bg-gradient-to-r from-indigo-500 to-violet-600";

    return (
      <div
        key="joining-fee-composite"
        className={`transform transition-all duration-300 ${loading ? "opacity-80" : ""}`}
      >
        <div className={`${gradient} ${cardClass}`}>
          <div className={`flex items-center justify-between ${compact ? "mb-2" : "mb-3"}`}>
            <div className="min-w-0">
              <p className="text-xs  tracking-wider text-white/95">
                Total Joining Fee
              </p>
              {loading ? (
                <div className="h-8 w-24 bg-white/30 rounded animate-pulse mt-1" />
              ) : (
                <p className={`${compact ? "text-2xl" : "text-3xl"} font-bold text-white mt-0.5`}>
                  {formatCurrency(totalJoiningFeeAmount)}
                </p>
              )}
              {!loading && (
                <p className="text-[10px] text-white/80 mt-0.5">
                  {(paidDriverCount + pendingDriverCount).toLocaleString("en-IN")} drivers total
                </p>
              )}
            </div>
            <div className={iconWrapClass}>
              <FaHandHoldingUsd className={`${iconClass} text-indigo-200`} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2 mt-2">
            <button
              type="button"
              onClick={() => handleJoiningFeeSubClick("paid")}
              disabled={loading}
              className="rounded-lg bg-white/14 border border-white/20 px-2 py-2 text-center transition-all duration-150 cursor-pointer hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/70 disabled:cursor-wait"
            >
              <p className="text-[11px] leading-4 text-white/90">Paid</p>
              <p className={`${compact ? "text-xl" : "text-2xl"} font-bold leading-tight mt-1 text-white`}>
                {loading ? (
                  <span className="inline-block h-7 w-16 rounded bg-white/30 animate-pulse" />
                ) : (
                  formatCurrency(paidAmount)
                )}
              </p>
              {!loading && (
                <p className="text-[11px] text-white/85 mt-0.5">
                  {paidDriverCount.toLocaleString("en-IN")} drivers
                </p>
              )}
            </button>

            <button
              type="button"
              onClick={() => handleJoiningFeeSubClick("pending")}
              disabled={loading}
              className="rounded-lg bg-white/14 border border-white/20 px-2 py-2 text-center transition-all duration-150 cursor-pointer hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/70 disabled:cursor-wait"
            >
              <p className="text-[11px] leading-4 text-white/90">Pending</p>
              <p className={`${compact ? "text-xl" : "text-2xl"} font-bold leading-tight mt-1 text-white`}>
                {loading ? (
                  <span className="inline-block h-7 w-16 rounded bg-white/30 animate-pulse" />
                ) : (
                  formatCurrency(pendingAmount)
                )}
              </p>
              {!loading && (
                <p className="text-[11px] text-white/85 mt-0.5">
                  {pendingDriverCount.toLocaleString("en-IN")} drivers
                </p>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderStatusCard = (status) => {
    const count = summaryData?.[status.key] || 0;
    const previousCount = showComparison
      ? Number(comparisonData?.[status.key] || 0)
      : null;
    const percentChange =
      showComparison && previousCount !== null
        ? calcPercentChange(count, previousCount)
        : null;
    const config = statusConfig[status.key];
    const IconComponent = config.icon;
    const isActive = activeStatus === status.key;

    return (
      <div
        key={status.key}
        onClick={() => handleClick(status.key)}
        className={`group transform transition-all duration-300 ${loading ? "cursor-wait opacity-80" : "cursor-pointer"} ${compact ? "hover:scale-[1.01]" : "hover:scale-105 hover:shadow-2xl"}`}
      >
        <div className={`${config.gradient} ${cardClass} ${isActive ? "ring-4 ring-offset-2 ring-blue-300" : ""}`}>
          <div className={`flex items-center justify-between ${compact ? "mb-1.5" : "mb-4"}`}>
            <div className={iconWrapClass}>
              <IconComponent className={`${iconClass} ${config.iconColor}`} />
            </div>
            <div className={badgeClass}>
              <span className="text-white font-semibold text-xs">
                {((count / denominator) * 100).toFixed(0)}%
              </span>
            </div>
          </div>

          <div className={compact ? "mb-0.5" : "mb-3"}>
            {loading ? (
              <div className="h-8 w-16 bg-white/30 rounded animate-pulse mb-2" />
            ) : (
              <h3 className={`${countClass} font-bold ${config.textColor} mb-1`}>
                {count.toLocaleString()}
              </h3>
            )}
            <p className={`text-sm ${config.textColor} opacity-90`}>{status.label}</p>
          </div>

          <div className="flex items-center justify-between">
            <span className={`text-xs ${config.textColor} opacity-75`}>
              {config.description}
            </span>
            <FaArrowRight
              className={`${compact ? "text-xs" : "text-sm"} ${config.textColor} ${loading ? "opacity-0" : "opacity-0 group-hover:opacity-100"} transition-opacity duration-300`}
            />
          </div>

          <div className={progressWrapClass}>
            <div
              className="h-full bg-white bg-opacity-40 rounded-full transition-all duration-500"
              style={{
                width: `${Math.min((count / denominator) * 100, 100)}%`,
              }}
            />
          </div>

          {showComparison && !loading && percentChange && (
            <div className="mt-2 pt-2 border-t border-white/30 flex items-center justify-between gap-2 text-xs">
              <div className={`${config.textColor} opacity-90 min-w-0`}>
                <span className="opacity-80">vs </span>
                <span className="font-semibold">{previousCount.toLocaleString()}</span>
              </div>
              <div
                className={`flex items-center justify-end gap-1 font-semibold whitespace-nowrap shrink-0 ${
                  percentChange.direction === "down"
                    ? "text-red-200"
                    : percentChange.direction === "flat"
                      ? "text-white/80"
                      : "text-emerald-200"
                }`}
              >
                <span>
                  {formatSignedDiff(count, previousCount, (v) =>
                    Number(v).toLocaleString("en-IN")
                  )}
                </span>
                {percentChange.direction === "down" ? (
                  <FaArrowDown className="text-[10px]" />
                ) : percentChange.direction === "up" ? (
                  <FaArrowUp className="text-[10px]" />
                ) : null}
                <span>{percentChange.display}</span>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className={classes}>
      {statuses.map((status) => renderStatusCard(status))}
      {includeRegistered && renderJoiningFeeCard()}
    </div>
  );
};

export default DriverStatusSummaryCards;
